(ns practitest.frontend.components2.pagination
  (:require [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.components.grid.server :as server]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.common.i18n :refer [get-translation
                                                     format-translation]]
            [practitest.frontend.common.utils :refer [register-components!]]))

(defn get-total-pages [total-count per-page]
  (let [total-pages (int (/ total-count per-page))]
    (if (< (* total-pages per-page) total-count)
      (inc total-pages)
      total-pages)))

(defn get-page-indexes
  [total-count current-page per-page]
  [(* (dec current-page) per-page)
   (min (* current-page (* per-page))
        total-count)])

(defn get-current-page-items
  [items current-page per-page]
  (if (seq items)
    (apply subvec items (get-page-indexes (count items) current-page per-page))
    items))

;; Calculate buttons layout
(defn build-pagination-buttons [total-count per-page max-buttons current-page always-show-nav?]
  (let [total-pages (get-total-pages total-count per-page)
        min-first-part-buttons 2
        min-last-part-buttons 2
        middle-part-buttons (int (/ (- max-buttons
                                       min-first-part-buttons
                                       min-last-part-buttons 2)
                                    2))]
    ;; TODO: sanity check for max-buttons ?

    (cond
      ;; Special case - no pagination required
      (<= total-pages 1)
      ;; Another sub-special case - no pagination required, but need to show :info section anyway
      (when (= max-buttons 0)
        (if always-show-nav?
          [:prev :info :next]
          [:info]))

      ;; Special case - no page buttons
      (= max-buttons 0)
      [:prev :info :next]

      ;; We have enough room to fit all buttons
      (<= total-pages max-buttons)
      (-> [:prev]
          (into
            (for [n (range 1 (inc total-pages))]
              n))
          (conj :next))

      :else
      (let [max-first-part (- max-buttons min-last-part-buttons 2)
            min-last-part (+ (- total-pages max-buttons)
                             min-first-part-buttons 3)

            ;; Three cases
            ; 1. Current-page is in the first-part
            ; 2. Current-page is in the middle-part
            ; 3. Current page is in the last part
            current-page-loc (cond
                               (<= current-page max-first-part)
                               :first-part

                               (>= current-page min-last-part)
                               :last-part

                               :else
                               :middle-part)

            first-part-end (case current-page-loc
                             :first-part
                             (- max-buttons min-last-part-buttons 1)

                             (:last-part :middle-part)
                             min-first-part-buttons)
            first-part (range 1 (inc first-part-end))

            middle-part (if (= :middle-part current-page-loc)
                          (apply conj [:ellipsis]
                                 (range (- current-page middle-part-buttons)
                                        (inc (+ current-page middle-part-buttons))))
                          [])

            last-part-start (case current-page-loc
                              (:first-part :middle-part)
                              (inc (- total-pages min-last-part-buttons))

                              :last-part
                              (dec min-last-part))
            last-part (apply conj [:ellipsis]
                             (range last-part-start (inc total-pages)))]

        (flatten
          [:prev
           first-part
           middle-part
           last-part
           :next])))))

(defn per-page-picker [{:keys [no-data?
                               local-state-updating
                               per-page] :as tableprops}]
  [:div.per-page-picker
   {:class (dom/classes-pairs-v :disabled no-data?)
    :on-click (when-not no-data?
                #(swap! local-state-updating update :show-per-row-selector? not))}
   (format-translation (get-translation "grid.pagination.show-n-rows"
                                        "%{n} rows")
                       {:n per-page})
   [icons/mux-chevron-down2]])

(defn per-page-dropdown [{:keys [per-page
                                 current-page
                                 total-count
                                 local-state-updating
                                 per-page-save
                                 current-page-save
                                 on-query-changed
                                 full-data?] :as tableprops}]
  (let [hide! #(swap! local-state-updating assoc :show-per-row-selector? false)]
    [:div.selector-dropdown.per-page-dropdown
     (for [i [10 20 50 100]]
       [:div.selector-item.auto-hover
        {:key      (str i)
         :class    (dom/classes-pairs-v :active (= per-page i))
         :on-click (fn []
                     (mixpanel/track "Grid Actions" {"Action" "Show Rows"})
                     (let [new-per-page i
                           new-current-page (if (< total-count (* new-per-page current-page))
                                              (inc (int (/ total-count new-per-page)))
                                              current-page)]
                       (swap! local-state-updating assoc :per-page new-per-page)

                       (when per-page-save
                         (per-page-save new-per-page))

                       (when current-page-save
                         (current-page-save new-current-page))

                       (when-not full-data?
                           ;; TODO: refactor
                           (on-query-changed (server/build-query
                                               (assoc tableprops
                                                 :per-page new-per-page)
                                               new-current-page)))
                       (hide!)
                       ))}
        (str i)])]))

(defn
  ^{:component? true}
  pagination-info-span
  "Informational span for pagination"
  [total-count current-page per-page]
  [:span.pagination-info-span
   ;; TODO: translation
   (let [[start-index end-index] (get-page-indexes total-count current-page per-page)]
     (if (> total-count 0)
       (str (inc start-index) " - " end-index " of " total-count)
       "0 of 0"))])

;; TODO: direct input of page number (validation ?)
(defn
  ^{:component? true}
  pagination-view
  "Show standard pagination view.

  * `on-change` - callback that receives new page number
  * `current-page` - current page selected
  * `per-page` - number of items to shows per page
  * `max-buttons` - max amount of buttons to show on the UI
  * `always-show-nav?` - when true, always show navigation chevrons even with 1 page
  * `disabled?` - when true, grays out pagination and disables clicks"
  [{:keys [on-change

           current-page
           total-count
           per-page
           max-buttons
           always-show-nav?
           disabled?]}]
  (let [total-pages (int (/ total-count per-page))
        ;; Check for truncation
        total-pages (if (< (* total-pages per-page) total-count)
                      (inc total-pages)
                      total-pages)

        layout (build-pagination-buttons total-count per-page (or max-buttons 11) current-page always-show-nav?)]
    (into
      [:div.pagination-control
       {:class (dom/classes-pairs-v :disabled disabled?)}]
      (for [item layout]
        (cond
          (= :prev item)
          (let [btn-disabled? (or disabled? (<= current-page 1))]
            [:span.pagination-button.pagination-prev-page
             {:class (dom/classes-pairs-v :disabled btn-disabled?)
              :on-click (fn [e]
                          (when (and on-change
                                     current-page
                                     (not btn-disabled?)
                                     (> current-page 1))
                            (on-change (dec current-page))))}
             [icons/mux-chevron-left]])

          (= :ellipsis item)
          [:div.pagination-item
           "…"]

          (= :info item)
          [pagination-info-span total-count current-page per-page]

          (int? item)
          ^{:key item}
          [:div.pagination-item
           {:class (dom/classes-pairs
                     :active (= item current-page))
            :on-click (fn [e]
                        (when (and on-change (not disabled?))
                          (on-change item)))}
           [:span.page-number
            item]]

          (= :next item)
          (let [btn-disabled? (or disabled? (>= current-page total-pages))]
            [:span.pagination-button.pagination-next-page
             {:class (dom/classes-pairs-v :disabled btn-disabled?)
              :on-click (fn [e]
                          (when (and on-change
                                     current-page
                                     (not btn-disabled?))
                            (on-change (inc current-page))))}
             [icons/mux-chevron-right]]))))))

(register-components!)
